import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import LogLayout from "../components/LogLayout";
import { Building2, Plus, Trash2, X, Phone, Mail, Clock, Save, Loader2, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const EMPTY_FORM = { name: "", code: "", contact_name: "", contact_phone: "", contact_email: "", address: "", payment_terms_days: 30, check_availability_days: 3, notes: "" };

export default function LogInsurersPage() {
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingInsurer, setEditingInsurer] = useState(null); // full edit modal
  const [editingDelay, setEditingDelay] = useState(null);
  const [editDelayValue, setEditDelayValue] = useState("");
  const [savingDelay, setSavingDelay] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/log/insurers`, { headers: getHeaders() });
      setInsurers(res.data.insurers || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/log/insurers`, { ...form, payment_terms_days: parseInt(form.payment_terms_days), check_availability_days: parseInt(form.check_availability_days || 3) }, { headers: getHeaders() });
      toast.success("Assureur ajouté");
      setShowCreate(false);
      setForm({ name: "", code: "", contact_name: "", contact_phone: "", contact_email: "", address: "", payment_terms_days: 30, check_availability_days: 3, notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet assureur ?")) return;
    try {
      await axios.delete(`${API}/log/insurers/${id}`, { headers: getHeaders() });
      toast.success("Assureur supprimé");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleSaveDelay = async (id) => {
    const days = parseInt(editDelayValue);
    if (!days || days < 1 || days > 365) return toast.error("Délai invalide (1-365 jours)");
    setSavingDelay(id);
    try {
      await axios.put(`${API}/log/insurers/${id}`, { payment_terms_days: days }, { headers: getHeaders() });
      toast.success(`Délai mis à jour : ${days} jours`);
      setEditingDelay(null);
      load();
    } catch (e) { toast.error("Erreur"); }
    finally { setSavingDelay(null); }
  };

  return (
    <LogLayout title="Assureurs" subtitle="Gestion des compagnies d'assurance partenaires">
      <div data-testid="log-insurers-page" className="space-y-4">
        <div className="flex justify-end">
          <Button data-testid="create-insurer-btn" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2">
            <Plus className="w-4 h-4" /> Nouvel assureur
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : insurers.length === 0 ? (
          <div className="text-center py-16 bg-slate-900 rounded-xl border border-white/5">
            <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucun assureur enregistré</p>
            <Button data-testid="empty-create-insurer-btn" onClick={() => setShowCreate(true)} variant="outline" className="mt-4 text-blue-400 border-blue-500/30 hover:bg-blue-500/100/10">
              Ajouter votre premier assureur
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {insurers.map(ins => (
              <div key={ins.id} data-testid={`insurer-card-${ins.id}`} className="bg-slate-900 border border-white/5 rounded-xl p-5 hover:border-blue-500/20 transition-colors group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/100/15 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">{ins.name}</h3>
                      {ins.code && <p className="text-[10px] text-slate-500 uppercase">{ins.code}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => setEditingInsurer(ins)} className="p-1.5 rounded-lg hover:bg-blue-500/10 text-slate-400 hover:text-blue-400 transition-all" title="Modifier" data-testid={`edit-insurer-${ins.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(ins.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-all" data-testid={`delete-insurer-${ins.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {ins.contact_name && <p className="text-xs text-slate-400 mb-1">{ins.contact_name}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {ins.contact_phone && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-500 bg-slate-800 px-2 py-1 rounded"><Phone className="w-3 h-3" />{ins.contact_phone}</span>
                  )}
                  {ins.contact_email && (
                    <span className="flex items-center gap-1 text-[10px] text-slate-500 bg-slate-800 px-2 py-1 rounded"><Mail className="w-3 h-3" />{ins.contact_email}</span>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  {editingDelay === ins.id ? (
                    <div className="flex items-center gap-2 w-full">
                      <Clock className="w-3.5 h-3.5 text-cyan-400" />
                      <input type="number" value={editDelayValue} onChange={e => setEditDelayValue(e.target.value)}
                        className="w-20 bg-slate-950 border border-cyan-500/30 rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none"
                        min={1} max={365} data-testid={`delay-input-${ins.id}`} />
                      <span className="text-[10px] text-slate-500">jours</span>
                      <button onClick={() => handleSaveDelay(ins.id)} disabled={savingDelay === ins.id}
                        className="ml-auto p-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                        data-testid={`save-delay-${ins.id}`}>
                        {savingDelay === ins.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => setEditingDelay(null)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingDelay(ins.id); setEditDelayValue(String(ins.payment_terms_days || 30)); }}
                      className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-cyan-400 transition-colors cursor-pointer"
                      data-testid={`edit-delay-${ins.id}`}>
                      <Clock className="w-3 h-3" /> Délai paiement : <span className="font-bold text-cyan-400">{ins.payment_terms_days}j</span> + <span className="font-bold text-emerald-400">{ins.check_availability_days || 3}j dispo</span>
                      <span className="text-[9px] opacity-0 group-hover:opacity-100 text-cyan-500 ml-1">(modifier)</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="create-insurer-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">Nouvel assureur</h2>
                <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs text-slate-400 block mb-1">Nom *</label>
                    <input data-testid="insurer-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs text-slate-400 block mb-1">Code</label>
                    <input data-testid="insurer-code-input" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Nom du contact</label>
                  <input data-testid="insurer-contact-name-input" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Téléphone</label>
                    <input data-testid="insurer-phone-input" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Email</label>
                    <input data-testid="insurer-email-input" type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Adresse</label>
                  <input data-testid="insurer-address-input" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Délai de paiement (jours)</label>
                  <input data-testid="insurer-terms-input" type="number" value={form.payment_terms_days} onChange={e => setForm(f => ({ ...f, payment_terms_days: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Délai disponibilité chèque (jours)</label>
                  <input data-testid="insurer-check-days-input" type="number" value={form.check_availability_days} onChange={e => setForm(f => ({ ...f, check_availability_days: e.target.value }))} placeholder="3" className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                  <p className="text-[10px] text-slate-500 mt-1">Jours après traitement pour la mise à disposition du chèque/virement</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Notes</label>
                  <textarea data-testid="insurer-notes-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
                  <Button type="submit" data-testid="submit-insurer-btn" className="flex-1 bg-blue-600 hover:bg-blue-500/100 text-white">Creer</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingInsurer && (
          <EditInsurerModal
            insurer={editingInsurer}
            onClose={() => setEditingInsurer(null)}
            onSaved={() => { setEditingInsurer(null); load(); }}
          />
        )}
      </div>
    </LogLayout>
  );
}

function EditInsurerModal({ insurer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: insurer.name || "",
    code: insurer.code || "",
    contact_name: insurer.contact_name || "",
    contact_phone: insurer.contact_phone || "",
    contact_email: insurer.contact_email || "",
    address: insurer.address || "",
    payment_terms_days: insurer.payment_terms_days ?? 30,
    check_availability_days: insurer.check_availability_days ?? 3,
    notes: insurer.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      await axios.put(`${API}/log/insurers/${insurer.id}`, {
        ...form,
        payment_terms_days: parseInt(form.payment_terms_days) || 30,
        check_availability_days: parseInt(form.check_availability_days) || 3,
      }, { headers: getHeaders() });
      toast.success("Assureur mis à jour");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="edit-insurer-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Pencil className="w-4 h-4 text-blue-400" /> Modifier l'assureur
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="edit-insurer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs text-slate-400 block mb-1">Nom *</label>
              <input data-testid="edit-insurer-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs text-slate-400 block mb-1">Code</label>
              <input data-testid="edit-insurer-code-input" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Nom du contact</label>
            <input data-testid="edit-insurer-contact-name-input" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Téléphone</label>
              <input data-testid="edit-insurer-phone-input" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Email</label>
              <input data-testid="edit-insurer-email-input" type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Adresse</label>
            <input data-testid="edit-insurer-address-input" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Délai de paiement (j)</label>
              <input data-testid="edit-insurer-terms-input" type="number" value={form.payment_terms_days} onChange={e => setForm(f => ({ ...f, payment_terms_days: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Délai dispo chèque (j)</label>
              <input data-testid="edit-insurer-check-days-input" type="number" value={form.check_availability_days} onChange={e => setForm(f => ({ ...f, check_availability_days: e.target.value }))} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Notes</label>
            <textarea data-testid="edit-insurer-notes-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="edit-insurer-submit-btn" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
